import React, { useState, useEffect, useCallback } from 'react';
import { FiXCircle, FiAlertTriangle, FiCheckCircle, FiRotateCw, FiShield, FiActivity } from 'react-icons/fi';
import api from '../../../../../services/api';
import './HealthTab.css';

const HealthTab = ({ block, onRefresh }) => {
  const [warnings, setWarnings] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [healthFilter, setHealthFilter] = useState('all');
  const [resolvingItems, setResolvingItems] = useState(new Set());

  // Fetch health data for the block
  const fetchHealthData = useCallback(async () => {
    if (!block?.serial) return;
    
    setLoading(true);
    try {
      const response = await api.block.getBlockHealth(block.serial);
      if (response.success) {
        setWarnings(response.data.warnings || []);
        setErrors(response.data.errors || []);
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
      
      // Check if it's a CORS/network error and provide fallback
      if (error.message.includes('CORS') || error.message.includes('Failed to fetch') || error.message.includes('Network error')) {
        // Provide mock data for development/testing
        setWarnings([
          {
            id: 'mock-warning-1',
            type: 'CONNECTIVITY',
            message: 'Health monitoring temporarily unavailable - API endpoint not accessible',
            severity: 'warning',
            timestamp: new Date().toISOString(),
            resolved: false,
            recommendation: 'Check network connectivity and API configuration'
          }
        ]);
        setErrors([
          {
            id: 'mock-error-1',
            type: 'API_ERROR',
            message: 'Health data API not available',
            severity: 'error',
            timestamp: new Date().toISOString(),
            resolved: false,
            recommendation: 'Contact system administrator to configure health monitoring endpoints'
          }
        ]);
      }
    } finally {
      setLoading(false);
    }
  }, [block?.serial]);

  // Auto-fetch health data when component mounts
  useEffect(() => {
    fetchHealthData();
  }, [fetchHealthData]);

  // Filter health items based on selected filter
  const filterHealthItems = (items, filter) => {
    switch (filter) {
      case 'active':
        return items.filter(item => !item.resolved);
      case 'resolved':
        return items.filter(item => item.resolved);
      default:
        return items;
    }
  };

  // Handle resolving individual warning
  const handleResolveWarning = async (warningId) => {
    setResolvingItems(prev => new Set(prev).add(`warning-${warningId}`));
    try {
      const response = await api.block.resolveWarning(block.serial, warningId);
      if (response.success) {
        setWarnings(prev => prev.map(w => 
          w.id === warningId 
            ? { ...w, resolved: true, resolvedAt: new Date().toISOString() }
            : w
        ));
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Error resolving warning:', error);
    } finally {
      setResolvingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(`warning-${warningId}`);
        return newSet;
      });
    }
  };

  // Handle resolving individual error
  const handleResolveError = async (errorId) => {
    setResolvingItems(prev => new Set(prev).add(`error-${errorId}`));
    try {
      const response = await api.block.resolveError(block.serial, errorId);
      if (response.success) {
        setErrors(prev => prev.map(e => 
          e.id === errorId 
            ? { ...e, resolved: true, resolvedAt: new Date().toISOString() }
            : e
        ));
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Error resolving error:', error);
    } finally {
      setResolvingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(`error-${errorId}`);
        return newSet;
      });
    }
  };

  // Handle resolving all warnings
  const handleResolveAllWarnings = async () => {
    const activeWarnings = warnings.filter(w => !w.resolved);
    if (activeWarnings.length === 0) return;

    setResolvingItems(prev => new Set(prev).add('warnings-all'));
    try {
      const response = await api.block.resolveAllWarnings(block.serial);
      if (response.success) {
        setWarnings(prev => prev.map(w => 
          !w.resolved 
            ? { ...w, resolved: true, resolvedAt: new Date().toISOString() }
            : w
        ));
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Error resolving all warnings:', error);
    } finally {
      setResolvingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete('warnings-all');
        return newSet;
      });
    }
  };

  // Handle resolving all errors
  const handleResolveAllErrors = async () => {
    const activeErrors = errors.filter(e => !e.resolved);
    if (activeErrors.length === 0) return;

    setResolvingItems(prev => new Set(prev).add('errors-all'));
    try {
      const response = await api.block.resolveAllErrors(block.serial);
      if (response.success) {
        setErrors(prev => prev.map(e => 
          !e.resolved 
            ? { ...e, resolved: true, resolvedAt: new Date().toISOString() }
            : e
        ));
        if (onRefresh) onRefresh();
      }
    } catch (error) {
      console.error('Error resolving all errors:', error);
    } finally {
      setResolvingItems(prev => {
        const newSet = new Set(prev);
        newSet.delete('errors-all');
        return newSet;
      });
    }
  };

  // Get error recommendation based on error type
  const getErrorRecommendation = (errorType) => {
    const recommendations = {
      'SENSOR_FAILURE': 'Check sensor connections and restart device. If issue persists, contact support.',
      'TEMPERATURE_OUT_OF_RANGE': 'Verify device placement and ambient temperature. Check HVAC system.',
      'BATTERY_CRITICAL': 'Device requires immediate charging or power source check.',
      'CONNECTIVITY_LOST': 'Check network connection and signal strength. Restart if needed.',
      'DISPENSE_MECHANISM_FAILURE': 'Check for blockages in dispense mechanism. May require maintenance.',
      'PAYMENT_SYSTEM_ERROR': 'Check payment processing system and network connectivity.',
      'FIRMWARE_ERROR': 'Device firmware may need updating. Contact support for assistance.',
      'HARDWARE_MALFUNCTION': 'Hardware issue detected. Contact support for repair/replacement.'
    };
    return recommendations[errorType] || 'Contact support for assistance with this issue.';
  };

  // Get warning recommendation based on warning type
  const getWarningRecommendation = (warningType) => {
    const recommendations = {
      'LOW_BATTERY': 'Monitor battery level and plan charging cycle.',
      'HIGH_TEMPERATURE': 'Monitor temperature and ensure proper ventilation.',
      'LOW_SUNSCREEN_LEVEL': 'Schedule refill soon to avoid service interruption.',
      'CONNECTIVITY_INTERMITTENT': 'Check network stability and signal strength.',
      'SENSOR_CALIBRATION': 'Sensor may need recalibration. Monitor performance.',
      'MAINTENANCE_DUE': 'Schedule routine maintenance to prevent issues.',
      'USAGE_PATTERN_ANOMALY': 'Monitor usage patterns for unusual activity.'
    };
    return recommendations[warningType] || 'Monitor situation and take action if needed.';
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const filteredWarnings = filterHealthItems(warnings, healthFilter);
  const filteredErrors = filterHealthItems(errors, healthFilter);
  const activeWarnings = warnings.filter(w => !w.resolved);
  const activeErrors = errors.filter(e => !e.resolved);

  return (
    <div className="health-tab">
      <div className="health-header">
        <div className="health-status-summary">
          <div className="health-stat">
            <FiXCircle className="health-icon error" />
            <span className="health-count">{activeErrors.length}</span>
            <span className="health-label">Active Errors</span>
          </div>
          <div className="health-stat">
            <FiAlertTriangle className="health-icon warning" />
            <span className="health-count">{activeWarnings.length}</span>
            <span className="health-label">Active Warnings</span>
          </div>
          <div className="health-stat">
            <FiShield className="health-icon success" />
            <span className="health-count">{warnings.filter(w => w.resolved).length + errors.filter(e => e.resolved).length}</span>
            <span className="health-label">Resolved</span>
          </div>
        </div>
        
        <div className="health-controls">
          <div className="health-filter-controls">
            <button 
              className={`filter-button ${healthFilter === 'all' ? 'active' : ''}`}
              onClick={() => setHealthFilter('all')}
            >
              All
            </button>
            <button 
              className={`filter-button ${healthFilter === 'active' ? 'active' : ''}`}
              onClick={() => setHealthFilter('active')}
            >
              Active
            </button>
            <button 
              className={`filter-button ${healthFilter === 'resolved' ? 'active' : ''}`}
              onClick={() => setHealthFilter('resolved')}
            >
              Resolved
            </button>
          </div>
          
          <button 
            className="refresh-button"
            onClick={fetchHealthData}
            disabled={loading}
          >
            <FiRotateCw className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      <div className="health-sections">
        {/* Errors Section */}
        <div className="health-section errors-section">
          <div className="health-section-header">
            <h4>
              <FiXCircle className="section-icon error" />
              Critical Errors
              <span className="section-count">({filteredErrors.length})</span>
            </h4>
            {activeErrors.length > 1 && (
              <button 
                className="resolve-all-button error-resolve-all"
                onClick={handleResolveAllErrors}
                disabled={resolvingItems.has('errors-all')}
              >
                {resolvingItems.has('errors-all') ? (
                  <>
                    <div className="spinner"></div>
                    Resolving...
                  </>
                ) : (
                  <>
                    <FiCheckCircle />
                    Resolve All
                  </>
                )}
              </button>
            )}
          </div>
          
          <div className="health-items">
            {filteredErrors.length === 0 ? (
              <div className="no-health-items">
                <FiCheckCircle className="success-icon" />
                <p>No {healthFilter === 'all' ? '' : healthFilter} errors found</p>
              </div>
            ) : (
              filteredErrors.map((error) => (
                <div key={error.id} className={`health-item error-item ${error.resolved ? 'resolved' : 'active'}`}>
                  <div className="health-item-header">
                    <div className="health-item-title">
                      <FiXCircle className="health-item-icon" />
                      <span className="error-type">{error.errorType}</span>
                      {error.count > 1 && <span className="health-count-badge">{error.count}</span>}
                      {error.resolved && <span className="resolution-badge">✅ Resolved</span>}
                    </div>
                    {!error.resolved && (
                      <button 
                        className="resolve-button error-resolve"
                        onClick={() => handleResolveError(error.id)}
                        disabled={resolvingItems.has(`error-${error.id}`)}
                      >
                        {resolvingItems.has(`error-${error.id}`) ? (
                          <>
                            <div className="spinner"></div>
                            Resolving...
                          </>
                        ) : (
                          'Resolve'
                        )}
                      </button>
                    )}
                  </div>
                  <div className="health-item-content">
                    <p className="health-message">{error.errorMessage}</p>
                    <div className="health-item-meta">
                      <span className="health-timestamp">
                        {error.resolved ? 'Resolved' : 'Detected'}: {formatTimestamp(error.timestamp)}
                      </span>
                      {error.previousMode && (
                        <span className="health-mode">Previous Mode: {error.previousMode}</span>
                      )}
                    </div>
                    <div className="health-recommendation">
                      💡 <strong>Immediate Action Required:</strong> {getErrorRecommendation(error.errorType)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Warnings Section */}
        <div className="health-section warnings-section">
          <div className="health-section-header">
            <h4>
              <FiAlertTriangle className="section-icon warning" />
              Warnings
              <span className="section-count">({filteredWarnings.length})</span>
            </h4>
            {activeWarnings.length > 1 && (
              <button 
                className="resolve-all-button warning-resolve-all"
                onClick={handleResolveAllWarnings}
                disabled={resolvingItems.has('warnings-all')}
              >
                {resolvingItems.has('warnings-all') ? (
                  <>
                    <div className="spinner"></div>
                    Resolving...
                  </>
                ) : (
                  <>
                    <FiCheckCircle />
                    Resolve All
                  </>
                )}
              </button>
            )}
          </div>
          
          <div className="health-items">
            {filteredWarnings.length === 0 ? (
              <div className="no-health-items">
                <FiCheckCircle className="success-icon" />
                <p>No {healthFilter === 'all' ? '' : healthFilter} warnings found</p>
              </div>
            ) : (
              filteredWarnings.map((warning) => (
                <div key={warning.id} className={`health-item warning-item ${warning.resolved ? 'resolved' : 'active'}`}>
                  <div className="health-item-header">
                    <div className="health-item-title">
                      <FiAlertTriangle className="health-item-icon" />
                      <span className="warning-type">{warning.warningType}</span>
                      {warning.count > 1 && <span className="health-count-badge">{warning.count}</span>}
                      {warning.resolved && <span className="resolution-badge">✅ Resolved</span>}
                    </div>
                    {!warning.resolved && (
                      <button 
                        className="resolve-button warning-resolve"
                        onClick={() => handleResolveWarning(warning.id)}
                        disabled={resolvingItems.has(`warning-${warning.id}`)}
                      >
                        {resolvingItems.has(`warning-${warning.id}`) ? (
                          <>
                            <div className="spinner"></div>
                            Resolving...
                          </>
                        ) : (
                          'Resolve'
                        )}
                      </button>
                    )}
                  </div>
                  <div className="health-item-content">
                    <p className="health-message">{warning.warningMessage}</p>
                    <div className="health-item-meta">
                      <span className="health-timestamp">
                        {warning.resolved ? 'Resolved' : 'Detected'}: {formatTimestamp(warning.timestamp)}
                      </span>
                    </div>
                    <div className="health-recommendation">
                      ⚠️ <strong>Recommended Action:</strong> {getWarningRecommendation(warning.warningType)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthTab;
